import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { DAYS_PT, addDays, fmtDM, ItemPreview, MK_STATUS } from "@/lib/marketing";
import { usePhotos, PhotoGrid, PhotoUploadButton } from "@/components/MarketingPhotos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Camera, Lightbulb, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = ["type", "day", "template", "photo_id", "headline", "tip_body", "text", "hashtags", "status", "theme"];
const pick = (it) => Object.fromEntries(FIELDS.map((k) => [k, it[k] ?? (k === "photo_id" ? null : "")]));

export function ItemEditor({ item, monday, brandVersion, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState(false);
  const [photos, reloadPhotos] = usePhotos();
  useEffect(() => { if (item) setF(pick(item)); }, [item]);
  if (!item || !f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/marketing/items/${item.id}`, f);
      toast.success("Item guardado");
      onSaved(data);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.put(`/marketing/items/${item.id}`, f);
      const { data } = await api.post(`/marketing/items/${item.id}/regenerate`);
      setF(pick(data));
      onSaved(data, true);
      toast.success("Nova versão gerada");
    } catch (err) { toast.error(apiError(err)); } finally { setRegen(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="mk-item-editor">
        <DialogHeader>
          <DialogTitle>{f.type === "post" ? "Publicação (feed)" : "Story"} · {DAYS_PT[(new Date(`${f.day}T12:00:00`).getDay() + 6) % 7]} {fmtDM(f.day)}</DialogTitle>
          <DialogDescription>Escolha a foto, o texto na imagem, a legenda e o estado.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 md:grid-cols-[260px_1fr]">
          <div className="space-y-2">
            <ItemPreview item={item} version={brandVersion} />
            <p className="text-center text-[11px] text-muted-foreground">Pré-visualização do último guardado · {f.type === "post" ? "1080×1350" : "1080×1920"}</p>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger data-testid="mk-edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="post">Post</SelectItem><SelectItem value="story">Story</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Dia previsto</Label>
                <Select value={f.day} onValueChange={(v) => setF({ ...f, day: v })}>
                  <SelectTrigger data-testid="mk-edit-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS_PT.map((d, i) => <SelectItem key={d} value={addDays(monday, i)}>{d} {fmtDM(addDays(monday, i))}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Estado</Label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger data-testid="mk-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>

            {f.theme && <p className="text-xs text-muted-foreground" data-testid="mk-edit-theme">Tema: <b>{f.theme}</b></p>}
            <div className="grid grid-cols-2 gap-2">
              {[["photo", Camera, "Foto da obra", "A sua foto com a marca por cima"], ["tip", Lightbulb, "Dica (visual de texto)", "Texto sobre fundo da marca, sem foto"]].map(([k, I, t, s]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, template: k })} data-testid={`mk-template-${k}`}
                  className={`flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${f.template === k ? "border-primary bg-accent" : "border-border hover:bg-secondary"}`}>
                  <I size={20} className="mt-0.5 shrink-0 text-primary" /><span><b>{t}</b><br /><span className="text-xs text-muted-foreground">{s}</span></span>
                </button>
              ))}
            </div>

            {f.template === "photo" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between"><Label>Foto</Label>
                  <PhotoUploadButton onUploaded={reloadPhotos} label="Carregar fotos" testid="mk-edit-upload" /></div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-border p-2">
                  <PhotoGrid photos={photos} selected={f.photo_id} onSelect={(id) => setF({ ...f, photo_id: f.photo_id === id ? null : id })} />
                </div>
              </div>
            )}

            <div className="space-y-1.5"><Label>{f.template === "tip" ? "Título da dica (na imagem)" : "Texto na imagem (opcional)"}</Label>
              <Input value={f.headline} maxLength={140} onChange={set("headline")} placeholder={f.template === "tip" ? "Comment entretenir vos pavés" : "Avant / Après"} data-testid="mk-edit-headline" /></div>
            {f.template === "tip" && (
              <div className="space-y-1.5"><Label>Texto da dica (na imagem)</Label>
                <Textarea rows={2} maxLength={400} value={f.tip_body} onChange={set("tip_body")} data-testid="mk-edit-tip-body" /></div>
            )}
            <div className="space-y-1.5"><Label>Legenda</Label>
              <Textarea rows={5} value={f.text} onChange={set("text")} placeholder="Texte de la publication… Demandez votre devis gratuit !" data-testid="mk-edit-text" /></div>
            <div className="space-y-1.5"><Label>Hashtags</Label>
              <Input value={f.hashtags} onChange={set("hashtags")} placeholder="#pavage #vaud #lausanne" data-testid="mk-edit-hashtags" /></div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" className="rounded-full" onClick={regenerate} disabled={regen || busy} data-testid="mk-edit-regenerate">
            {regen ? <CircleNotch className="mr-1.5 animate-spin" /> : <Sparkle size={16} className="mr-1.5" />} Gerar de novo (IA)
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} disabled={busy || regen} data-testid="mk-edit-save">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
